import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  UserCheck, Clock, CheckCircle2, XCircle, Trash2, Mail, Phone, MapPin, Calendar,
  Building2, Briefcase, FileText, AlertTriangle, RefreshCw, Eye, UserIcon
} from 'lucide-react';
import { cn } from '../../lib/utils';

const STATUS_BADGE = {
  pending: { label: 'En attente', icon: Clock, cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border-amber-200 dark:border-amber-900/50' },
  approved: { label: 'Validée', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border-emerald-200 dark:border-emerald-900/50' },
  rejected: { label: 'Refusée', icon: XCircle, cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200 border-rose-200 dark:border-rose-900/50' },
};

const PRO_LABEL = {
  salarie: 'Salarié(e) en auto-école',
  independant: 'Moniteur indépendant',
  formateur_ccs: 'Formateur CCS',
  autre: 'Autre',
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const Icon = s.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border', s.cls)}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function ApplicationDetail({ app, onApprove, onReject, onDelete }) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const doApprove = async () => {
    if (!window.confirm(`Valider la candidature de ${app.first_name} ${app.last_name} ?\n\nUn compte enseignant sera créé avec l'email ${app.email}.`)) return;
    setBusy(true);
    try {
      await onApprove(app.id);
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (reason.trim().length < 5) {
      toast.error('Le motif doit faire au moins 5 caractères');
      return;
    }
    setBusy(true);
    try {
      await onReject(app.id, reason.trim());
      setRejectOpen(false);
      setReason('');
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm('Supprimer définitivement cette candidature ? Cette action est irréversible.')) return;
    setBusy(true);
    try {
      await onDelete(app.id);
    } finally { setBusy(false); }
  };

  return (
    <div className="mc-card p-5 space-y-4" data-testid={`ta-card-${app.id}`}>
      {/* Header : identité + statut */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-black tracking-tight">
              {app.first_name} {app.last_name}
            </h3>
            <StatusBadge status={app.status} />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Demande reçue le {new Date(app.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </div>
          {app.reviewed_at && (
            <div className="text-xs text-muted-foreground">
              Examinée le {new Date(app.reviewed_at).toLocaleDateString('fr-FR')} par {app.reviewed_by}
            </div>
          )}
        </div>

        {app.status === 'pending' && (
          <div className="flex gap-2 flex-wrap flex-shrink-0">
            <Button
              onClick={doApprove}
              disabled={busy}
              size="sm"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1.5"
              data-testid={`ta-approve-${app.id}`}
            >
              <CheckCircle2 className="w-4 h-4" /> Valider
            </Button>
            <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full gap-1.5 border-rose-300 text-rose-700 dark:text-rose-300 dark:border-rose-900/50 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                  data-testid={`ta-reject-${app.id}`}
                >
                  <XCircle className="w-4 h-4" /> Refuser
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Refuser la candidature</DialogTitle>
                  <DialogDescription>
                    Expliquez brièvement pourquoi vous refusez la candidature de <strong>{app.first_name} {app.last_name}</strong>. Ce motif pourra être transmis au candidat.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-2">
                  <Label htmlFor="reject-reason">Motif *</Label>
                  <Textarea
                    id="reject-reason"
                    rows={4}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Ex : Document professionnel manquant, incohérence dans les informations, etc."
                    data-testid="ta-reject-reason-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum 5 caractères.</p>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setRejectOpen(false)} disabled={busy}>Annuler</Button>
                  <Button
                    onClick={doReject}
                    disabled={busy || reason.trim().length < 5}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="ta-reject-confirm"
                  >
                    Confirmer le refus
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {app.status !== 'pending' && (
          <Button
            onClick={doDelete}
            disabled={busy}
            size="sm"
            variant="ghost"
            className="rounded-full gap-1.5 text-muted-foreground hover:text-rose-600"
            data-testid={`ta-delete-${app.id}`}
          >
            <Trash2 className="w-4 h-4" /> Supprimer
          </Button>
        )}
      </div>

      {/* Contact + Naissance */}
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div className="flex items-start gap-2">
          <Mail className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Email</div>
            <a href={`mailto:${app.email}`} className="text-sm font-semibold hover:underline break-all">{app.email}</a>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Phone className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Téléphone</div>
            <a href={`tel:${app.phone}`} className="text-sm font-semibold hover:underline">{app.phone}</a>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Calendar className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Date de naissance</div>
            <div className="text-sm">{app.birth_date} · {app.birth_place}</div>
          </div>
        </div>
        {(app.address || app.city) && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Adresse</div>
              <div className="text-sm">
                {app.address && <div>{app.address}</div>}
                {(app.postal_code || app.city) && <div>{app.postal_code} {app.city}</div>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Statut pro */}
      <div className="rounded-xl bg-[hsl(var(--panel))] border border-border p-3 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="flex items-start gap-2">
          <Briefcase className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" />
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Statut pro</div>
            <div className="font-semibold">{PRO_LABEL[app.pro_status] || app.pro_status}</div>
          </div>
        </div>
        {app.school_name && (
          <div className="flex items-start gap-2">
            <Building2 className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" />
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Auto-école / structure</div>
              <div className="font-semibold">{app.school_name}</div>
            </div>
          </div>
        )}
        {app.siret && (
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">N° SIRET</div>
            <div className="font-mono text-xs">{app.siret}</div>
          </div>
        )}
        {app.years_experience !== null && app.years_experience !== undefined && (
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Expérience</div>
            <div className="font-semibold">{app.years_experience} année{app.years_experience > 1 ? 's' : ''}</div>
          </div>
        )}
      </div>

      {/* Message */}
      {app.message && (
        <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 p-3">
          <div className="flex items-start gap-2">
            <FileText className="w-4 h-4 mt-0.5 text-amber-700 dark:text-amber-300 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-1">Message du candidat</div>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap">{app.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Motif de refus si applicable */}
      {app.status === 'rejected' && app.rejection_reason && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-700 dark:text-rose-300 flex-shrink-0" />
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-rose-700 dark:text-rose-300 mb-1">Motif de refus</div>
              <p className="text-sm text-foreground/90">{app.rejection_reason}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminTeacherApplications() {
  const [data, setData] = useState({ applications: [], counts: { total: 0, pending: 0, approved: 0, rejected: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getTeacherApplications(filter === 'all' ? null : filter);
      setData(res);
    } catch (e) {
      toast.error(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const onApprove = async (id) => {
    try {
      const res = await api.approveTeacherApplication(id);
      toast.success(res.message || 'Candidature validée');
      load();
    } catch (e) { toast.error(e.message || 'Erreur'); }
  };

  const onReject = async (id, reason) => {
    try {
      await api.rejectTeacherApplication(id, reason);
      toast.success('Candidature refusée');
      load();
    } catch (e) { toast.error(e.message || 'Erreur'); }
  };

  const onDelete = async (id) => {
    try {
      await api.deleteTeacherApplication(id);
      toast.success('Candidature supprimée');
      load();
    } catch (e) { toast.error(e.message || 'Erreur'); }
  };

  const filters = [
    { k: 'pending', label: 'En attente', count: data.counts.pending },
    { k: 'approved', label: 'Validées', count: data.counts.approved },
    { k: 'rejected', label: 'Refusées', count: data.counts.rejected },
    { k: 'all', label: 'Toutes', count: data.counts.total },
  ];

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-teacher-applications-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
            <UserCheck className="w-3.5 h-3.5" /> Back-office admin
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Candidatures enseignants</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Examinez et validez les demandes d'accès des moniteurs. Un compte enseignant est créé automatiquement lors de la validation.
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={load} data-testid="ta-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Rafraîchir
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {filters.map(f => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`ta-filter-${f.k}`}
            className={cn(
              'mc-card p-3 sm:p-4 text-left transition-all hover:-translate-y-0.5',
              filter === f.k ? 'ring-2 ring-[hsl(var(--primary))] ring-offset-2 ring-offset-background' : ''
            )}
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{f.label}</div>
            <div className="text-2xl sm:text-3xl font-black mt-0.5 tabular-nums">{f.count}</div>
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="mc-card p-8 text-center text-muted-foreground">Chargement…</div>
      ) : data.applications.length === 0 ? (
        <div className="mc-card p-8 text-center" data-testid="ta-empty">
          <UserIcon className="w-10 h-10 mx-auto text-muted-foreground mb-3 opacity-40" />
          <div className="font-bold text-lg">Aucune candidature {filter === 'pending' ? 'en attente' : filter === 'approved' ? 'validée' : filter === 'rejected' ? 'refusée' : ''}</div>
          <p className="text-sm text-muted-foreground mt-1">
            {filter === 'pending'
              ? 'Les nouvelles demandes apparaîtront ici.'
              : 'Rien à afficher pour ce filtre.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.applications.map(app => (
            <ApplicationDetail
              key={app.id}
              app={app}
              onApprove={onApprove}
              onReject={onReject}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
